import React from 'react';
import {observer} from 'mobx-react';
import {TechseeBaseComponent} from '@techsee/techsee-ui-common/lib/_shared/techsee-base-component';
import {GenericModal} from '@techsee/techsee-ui-common/lib/modals/generic';
import {SimpleTabs, ITab} from '@techsee/techsee-ui-common/lib/tabs/simple';
import Translate from '../../components/translate/Translate';
import patterns from '@techsee/techsee-common/lib/constants/utils.patterns';
import tsDate from '@techsee/techsee-date';

import {CustomerHistoryModalController, IHistoryResource} from './CustomerHistoryModal.controller';

import './CustomerHistoryModal.style.scss';

interface Props {
    ctrl: CustomerHistoryModalController;
}

@observer
export class CustomerHistoryModal extends TechseeBaseComponent<Props> {
    renderInternal(): JSX.Element {
        const {ctrl} = this.props;
        if (!ctrl.isVisible || !ctrl.history) return <div />;

        return (
            <GenericModal
                className='use-common-styles customer-history-modal'
                show={ctrl.isVisible}
                isCloseVisible={false}
                onHideHandler={ctrl.close}
                header={() => this.renderHeader()}
                content={() => this.renderContent()}
                toolbar={() => null}
            />
        );
    }

    private renderHeader(): JSX.Element {
        const {ctrl} = this.props;
        return (
            <div className='history-header' dir={ctrl.dir}>
                <h4>
                    <Translate text='DASHBOARD.HISTORY.VIEW.CUSTOMER_HISTORY' />
                </h4>

                <button className='ts-remove-btn-light pull-right' onClick={() => ctrl.close()}>
                    &times;
                </button>
            </div>
        );
    }

    private renderContent(): JSX.Element {
        const tabs = this.buildTabs();
        const {ctrl} = this.props;

        return (
            <div className='history-body' dir={ctrl.dir}>
                <SimpleTabs uid='customer-history-tabs' tabsList={tabs} />
            </div>
        );
    }

    private buildTabs(): ITab[] {
        const {ctrl} = this.props;
        const history = ctrl.history!;

        return [
            {
                title: `${ctrl.translate('HISTORY.VIEW.IMAGES')} (${history.images})`,
                content: () => this.renderImagesTab()
            },
            {
                title: `${ctrl.translate('HISTORY.VIEW.VIDEOS')} (${history.videos})`,
                content: () => this.renderVideosTab()
            },
            {
                title: `${ctrl.translate('HISTORY.VIEW.MESSAGES')} (${history.messages})`,
                content: () => this.renderMessagesTab()
            },
            {
                title: `${ctrl.translate('HISTORY.VIEW.FILES')} (${history.files})`,
                content: () => this.renderFilesTab()
            }
        ];
    }
    private renderImagesTab(): JSX.Element {
        return this.renderMediaTab('images', 'HISTORY.VIEW.TEMP_IMAGE');
    }

    private renderVideosTab(): JSX.Element {
        return this.renderMediaTab('videos', 'HISTORY.VIEW.TEMP_VIDEO');
    }

    private renderMediaTab(mediaType: 'images' | 'videos', tempTranslationKey: string): JSX.Element {
        const {ctrl} = this.props;
        const {dir} = ctrl;
        const records = ctrl.history!.records;

        const renderMediaItem = (mediaItem: IHistoryResource, roomId: string, idx: number) => (
            <div key={idx} className='thumbnail-container'>
                <div
                    className='thumbnail'
                    onClick={() => mediaItem.storageType !== 'TEMP' && ctrl.annotateResource(mediaItem, roomId)}>
                    <img src={mediaItem.url} />
                    {mediaItem.storageType === 'TEMP' && (
                        <div className='caption temp'>
                            <p className='temp-caption'>
                                <Translate text={tempTranslationKey} />
                            </p>
                        </div>
                    )}
                </div>
            </div>
        );

        return (
            <div className='ts-history-images-list' dir={dir}>
                {records.map(
                    (record, i) =>
                        record[mediaType].length > 0 && (
                            <div key={i}>
                                <div className='ts-history-images-date'>
                                    <small>{this.formatDate(record.timestamp)}</small>
                                </div>
                                {record[mediaType].map((mediaItem, idx) =>
                                    renderMediaItem(mediaItem, record.roomId, idx)
                                )}
                            </div>
                        )
                )}
            </div>
        );
    }

    private renderMessagesTab(): JSX.Element {
        const {ctrl} = this.props;
        const {dir} = ctrl;
        const records = ctrl.history!.records;

        return (
            <ul className='list-group' dir={dir}>
                {records.map(
                    (record, i) =>
                        record.messages.length > 0 && (
                            <div key={i}>
                                <div className='list-group-date'>
                                    <small>{this.formatDate(record.timestamp)}</small>
                                </div>
                                {record.messages.map((msg, idx) => (
                                    <li
                                        key={idx}
                                        className='list-group-item'
                                        dangerouslySetInnerHTML={{
                                            __html: this.linkifyMessage(msg)
                                        }}
                                    />
                                ))}
                            </div>
                        )
                )}
            </ul>
        );
    }

    private renderFilesTab(): JSX.Element {
        const {ctrl} = this.props;
        const {dir} = ctrl;
        const records = ctrl.history!.records;

        return (
            <ul className='list-group' dir={dir}>
                {records.map(
                    (record, i) =>
                        record.files.length > 0 && (
                            <div key={i}>
                                <div className='list-group-date'>
                                    <small>{this.formatDate(record.timestamp)}</small>
                                </div>
                                {record.files.map((file, idx) => (
                                    <li key={idx} className='list-group-item'>
                                        <a href={file} target='_blank' rel='noreferrer'>
                                            {this.getBasename(file)}
                                        </a>
                                    </li>
                                ))}
                            </div>
                        )
                )}
            </ul>
        );
    }

    private formatDate(date: string): string {
        const {ctrl} = this.props;
        const locale = ctrl.getLocale();
        return tsDate.withLocale(tsDate(date), locale).format('MMMM D, YYYY h:mm A');
    }

    private linkifyMessage(message: string): string {
        if (typeof (window as any).TsUtils !== 'undefined' && (window as any).TsUtils.linkify) {
            return (window as any).TsUtils.linkify(message);
        }

        return message.replace(patterns.url, '<a href="$&" target="_blank" rel="noopener noreferrer">$&</a>');
    }

    private getBasename(filepath: string): string {
        if (filepath) {
            return filepath.split('?')[0].substring(filepath.lastIndexOf('/') + 1);
        }
        return filepath;
    }
}
